import { apiClient } from '@/lib/apiClient';
import type { ProductSummary } from '@/lib/api-admin/types';

/**
 * ID55_2 상품선택 팝업 검색 로더. catalog admin 상품 목록(/admin/v1/products)을 사용해
 * 상품명 키워드 + 페이지로 조회한다. SearchSelectField 의 SearchSelectOption 보다 풍부한
 * 표시 메타(판매가 listPrice)를 동반해야 하므로 별도 행 타입(ProductSearchRow)을 반환한다.
 */
const PAGE_SIZE = 20;

export interface ProductSearchRow {
  productId: string;
  name: string;
  brandName?: string | null;
  thumbnailUrl?: string | null;
  /** 카탈로그 판매가(KRW). 기본 단가/표시용. */
  salePrice: number | null;
}

export interface ProductSearchResult {
  rows: ProductSearchRow[];
  hasMore: boolean;
  total?: number;
}

interface ProductListPayload {
  items?: ProductSummary[];
  content?: ProductSummary[];
  totalElements?: number;
}

const toRow = (p: ProductSummary): ProductSearchRow => ({
  productId: p.productId,
  name: p.name,
  brandName: p.brandName ?? null,
  thumbnailUrl: p.thumbnailUrl || null,
  salePrice: p.salePrice?.amount ?? null,
});

/** 상품 검색(0-base page). keyword 빈 값이면 전체에서 페이지 단위 로드. */
export const searchProducts = async (
  keyword: string,
  page: number,
): Promise<ProductSearchResult> => {
  const trimmed = keyword.trim();
  const query: Record<string, unknown> = { size: PAGE_SIZE, page };
  if (trimmed) query.keyword = trimmed;

  const [data, meta] = await apiClient.request('get', '/admin/v1/products', {
    params: { query },
  });
  const payload = data as ProductListPayload;
  const rawItems = payload.items ?? payload.content ?? [];
  const rows = rawItems.map(toRow);

  const totalItems = meta?.pagination?.totalItems ?? payload.totalElements;
  const hasMore =
    totalItems != null
      ? (page + 1) * PAGE_SIZE < totalItems
      : rows.length === PAGE_SIZE;

  return totalItems != null ? { rows, hasMore, total: totalItems } : { rows, hasMore };
};
